/**
 * Renewal Shared Spacing Constants
 *
 * 이 파일은 shared 컴포넌트에서 사용할 간격(spacing) 상수들을 정의합니다.
 * MUI 테마와 Figma 디자인 시스템을 기반으로 구성되었습니다.
 */

// Base Spacing Unit (8px 기준)
export const SPACING_UNIT = 8;

// Spacing Scale (8px의 배수)
export const SPACING = {
  0: 0,
  1: SPACING_UNIT * 0.5, // 4px
  2: SPACING_UNIT * 1, // 8px
  3: SPACING_UNIT * 1.5, // 12px
  4: SPACING_UNIT * 2, // 16px
  5: SPACING_UNIT * 2.5, // 20px
  6: SPACING_UNIT * 3, // 24px
  7: SPACING_UNIT * 3.5, // 28px
  8: SPACING_UNIT * 4, // 32px
  9: SPACING_UNIT * 4.5, // 36px
  10: SPACING_UNIT * 5, // 40px
  11: SPACING_UNIT * 5.5, // 44px
  12: SPACING_UNIT * 6, // 48px
  14: SPACING_UNIT * 7, // 56px
  16: SPACING_UNIT * 8, // 64px
  20: SPACING_UNIT * 10, // 80px
  24: SPACING_UNIT * 12, // 96px
  28: SPACING_UNIT * 14, // 112px
  32: SPACING_UNIT * 16, // 128px
  36: SPACING_UNIT * 18, // 144px
  40: SPACING_UNIT * 20, // 160px
  44: SPACING_UNIT * 22, // 176px
  48: SPACING_UNIT * 24, // 192px
  52: SPACING_UNIT * 26, // 208px
  56: SPACING_UNIT * 28, // 224px
  60: SPACING_UNIT * 30, // 240px
  64: SPACING_UNIT * 32, // 256px
  72: SPACING_UNIT * 36, // 288px
  80: SPACING_UNIT * 40, // 320px
  96: SPACING_UNIT * 48, // 384px
} as const;

// Semantic Spacing (의미적 간격)
export const SEMANTIC_SPACING = {
  // Component 내부 간격
  componentPadding: SPACING[4], // 16px
  componentMargin: SPACING[6], // 24px

  // 레이아웃 간격
  sectionSpacing: SPACING[12], // 48px
  containerPadding: SPACING[6], // 24px

  // 그리드 간격
  gridGap: SPACING[4], // 16px
  gridGapLarge: SPACING[8], // 32px

  // 폼 요소 간격
  formFieldSpacing: SPACING[4], // 16px
  formGroupSpacing: SPACING[6], // 24px

  // 버튼 간격
  buttonSpacing: SPACING[2], // 8px
  buttonGroupSpacing: SPACING[3], // 12px

  // 리스트 간격
  listItemSpacing: SPACING[2], // 8px
  listSectionSpacing: SPACING[6], // 24px

  // 카드 간격
  cardPadding: SPACING[4], // 16px
  cardSpacing: SPACING[6], // 24px

  // 모달/다이얼로그 간격
  modalPadding: SPACING[6], // 24px
  modalSpacing: SPACING[4], // 16px

  // 헤더/푸터 간격
  headerPadding: SPACING[4], // 16px
  footerPadding: SPACING[6], // 24px
} as const;

// Border Radius
export const BORDER_RADIUS = {
  none: 0,
  xs: "2px",
  sm: "4px",
  md: "6px",
  lg: "8px",
  xl: "12px",
  xxl: "16px",
  xxxl: "24px",
  full: "9999px",
} as const;

// Shadows
export const SHADOWS = {
  none: "none",
  xs: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  sm: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  xxl: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
} as const;

// Z-Index Scale
export const Z_INDEX = {
  hide: -1,
  auto: "auto",
  base: 0,
  sideBarModal: 10,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

// Breakpoints (MUI 호환)
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;

// Container Max Widths
export const CONTAINER_MAX_WIDTHS = {
  xs: "100%",
  sm: "600px",
  md: "900px",
  lg: "1200px",
  xl: "1536px",
} as const;

// Legacy Spacing (기존 시스템과의 호환성)
export const LEGACY_SPACING = {
  // 기존 NAV 상수
  navWidth: "340px",
  navIconWidth: "56px",
  navPadding: "18px",

  // 기존 TABLE 상수
  tableRowHeight: 46,
} as const;

// 통합 Spacing 객체
export const SPACING_CONSTANTS = {
  unit: SPACING_UNIT,
  spacing: SPACING,
  semantic: SEMANTIC_SPACING,
  borderRadius: BORDER_RADIUS,
  shadows: SHADOWS,
  zIndex: Z_INDEX,
  breakpoints: BREAKPOINTS,
  containerMaxWidths: CONTAINER_MAX_WIDTHS,
  legacy: LEGACY_SPACING,
} as const;

// 타입 정의
export type SpacingKey = keyof typeof SPACING;
export type SemanticSpacingKey = keyof typeof SEMANTIC_SPACING;
export type BorderRadiusKey = keyof typeof BORDER_RADIUS;
export type ShadowKey = keyof typeof SHADOWS;
export type ZIndexKey = keyof typeof Z_INDEX;
export type BreakpointKey = keyof typeof BREAKPOINTS;
export type ContainerMaxWidthKey = keyof typeof CONTAINER_MAX_WIDTHS;
export type LegacySpacingKey = keyof typeof LEGACY_SPACING;
